<template>
    <v-dialog
        v-model="dialog"
        persistent
        width="600"
    >
        <v-card
            class="pa-4"
        >
            <v-card-title>
                Co-Curricular Clubs
                <v-spacer></v-spacer>
                <v-dialog
                    v-model="dialogAddClub"
                    max-width="500px"
                    persistent
                >
                    <template v-slot:activator="{ on, attrs }">
                        <v-btn
                            color="primary"
                            v-bind="attrs"
                            v-on="on"
                            small
                        >
                            Add Club
                        </v-btn>
                    </template>
                    <v-card>
                        <v-card-title>
                            <span class="headline">{{ formTitle }}</span>
                        </v-card-title>

                        <v-card-text>
                            <v-form 
                                v-model="valid"
                                ref="form"                                        
                            >
                                <v-container>
                                    <v-row>
                                        <v-col
                                            cols="12"                                                
                                        >
                                            <v-text-field
                                                v-model="editedClub.name"
                                                label="Club Name"
                                                :rules="rules"
                                                required
                                            ></v-text-field>
                                        </v-col>

                                    </v-row>
                                </v-container>
                            </v-form>    
                        </v-card-text>

                        <v-card-actions class="mr-3">
                            <v-spacer></v-spacer>
                            <v-btn
                                depressed
                                @click="close"
                                class="mr-4"
                            >
                                Cancel
                            </v-btn>
                            <v-btn
                                color="primary"
                                @click="addClub"
                            >
                                {{ buttonText }}
                            </v-btn>
                        </v-card-actions>
                    </v-card>
                </v-dialog>

            </v-card-title>

            <v-data-table
                :headers="headers"
                :items="clubs"
                :search="search"
                fixed-header
                height="50vh"
                :loading="loading"
            >
                <template v-slot:top>
                    <div class="mb-6 px-3">
                        <v-text-field
                            v-model="search"
                            append-icon="mdi-magnify"
                            label="Search"
                            single-line
                            hide-details
                            clearable
                        ></v-text-field>

                        <v-dialog
                            v-model="dialogDelete"
                            max-width="600px"
                            persistent
                        >
                            <v-card>
                                <v-card-title class="text-body-1">
                                    Delete {{ editedClub.name }} ?
                                </v-card-title>
                                <v-card-actions class="px-6">
                                    <v-spacer></v-spacer>
                                    <v-btn
                                        color="grey darken-1"
                                        @click="closeDelete"
                                        outlined
                                        small
                                    >Cancel</v-btn>
                                    <v-btn
                                        color="primary"
                                        @click="deleteClubConfirm"
                                        small
                                    >Delete</v-btn>
                                </v-card-actions>
                            </v-card>
                        </v-dialog>

                    </div>
                </template>

                <template v-slot:[`item.actions`]="{ item }">
                    <v-btn
                        color="primary"
                        x-small                        
                        class="mr-4"
                        @click="editClub(item)"
                    >
                        <v-icon
                            small 
                            left
                        >
                            mdi-pencil
                        </v-icon>
                        Edit
                    </v-btn>

                    <v-btn
                        outlined
                        x-small
                        @click="deleteClub(item)" 
                        color="grey darken-2"                      
                    >
                        <v-icon
                            small                           
                            left
                        >
                            mdi-delete
                        </v-icon>
                        Delete
                    </v-btn>    

                </template>
            </v-data-table>

            <v-overlay
                :value="overlay"
                opacity="0.8"
                z-index="10"
                absolute                    
            >
                
                <v-progress-circular
                    indeterminate
                    size="64"
                ></v-progress-circular>                   
            </v-overlay>

            <v-snackbar
                v-model="snackbar"
                timeout="5000"
                :color="snackColor"
            >
                {{ snackText }}
                <template v-slot:action="{ attrs }">
                    <v-btn
                        text
                        v-bind="attrs"
                        @click="snackbar = false"
                    >
                        Close
                    </v-btn>
                </template>
            </v-snackbar>

            <v-card-actions class="">
                <v-spacer></v-spacer>
                <v-btn
                    color="grey darken-1"
                    @click="closeAddEditSubjects"
                    outlined
                    small
                >Close</v-btn>
                
            </v-card-actions>

        </v-card>
    </v-dialog>
</template>

<script>
import { mapActions, mapMutations } from 'vuex';
export default {
    props: {
        dialogOpen: Boolean,
    },

    created () {
        this.initialize();
    },

    data: function () {
        return {
            dialog: this.dialogOpen,
            clubs: [],
            headers: [
                {text: 'Club', value: 'name'},
                {text: 'Actions', value: 'actions', width: '200', sortable: false},
            ],
            dialogAddClub: false,
            dialogDelete: false,
            search: '',
            editedIndex: -1,
            editedClub: {
               name: '',
            },
            defaultItem: {
                name: ''
            },
            loading: false,
            valid: false,
            rules: [v =>  !!v || 'Field cannot be blank'],
            overlay: false,
            snackbar: false,
            snackText: '',
            snackColor: '',
        }
        
    },

    computed: {
        formTitle () {
            return this.editedIndex === -1 ? 'New Club' : 'Edit Club'
        },

        buttonText () {
            return this.editedIndex === -1 ? 'Add Club' : 'Update Club'
        },
    },

    methods: {
        ...mapActions({
            getClubs: 'settings/getCocurricularClubs',
            postClub: 'settings/postCocurricularClub',
            delete: 'settings/deleteCocurricularClub',            
        }),

        ...mapMutations({
            setEditedClub: 'settings/setCocurricularClub',
        }),

        async initialize () {
            this.loading = true;
            try {
                const { data } = await this.getClubs();                
                this.clubs = data;               
            } catch (error) {
                error.response ? console.log(error.response) : console.log(error);
            }
            this.loading = false;
        },

        editClub (item) {
            this.editedIndex = this.clubs.indexOf(item);
            this.editedClub = Object.assign({}, item);
            this.dialogAddClub = true;            
        },

        deleteClub (item) {
            this.editedIndex = this.clubs.indexOf(item);
            this.editedClub = Object.assign({}, item);
            this.dialogDelete = true;           
        },

        async deleteClubConfirm () {
            this.setEditedClub(this.editedClub);
            this.snackText = `${this.editedClub.name} deleted`;
            this.closeDelete();

            this.$nextTick(() => {
                this.overlay = true;
            }) 

            try {
                await this.delete();               
                this.snackColor = "primary"; 
                this.initialize()
            } catch (error) {
                if(error.response) console.log(error.response);
                else console.log(error);
                let errorMessage = error.response ? error.response.data?.error : "An Error Has Occured, Could not delete the club";
                this.snackText = errorMessage;
                this.snackColor = "red";
            }            
            
            this.overlay = false;

            this.$nextTick(() => {
                this.snackbar = true;
            })
            
        },

        close () {
            this.dialogAddClub = false;
            this.resetValidation();
            this.$nextTick(() => {
                this.editedClub = Object.assign({}, this.defaultItem);
                this.editedIndex = -1
            })
        },

        closeDelete () {
            this.dialogDelete = false;
            this.$nextTick(() => {
                this.editedClub = Object.assign({}, this.defaultItem);
                this.editedIndex = -1;
            })
        },

        async addClub () {
            this.$refs.form.validate();
            // console.log(`Valid: ${this.valid}`);
            if(!this.valid) return;
            this.close();

            this.overlay = true;
            try {
                this.setEditedClub(this.editedClub);
                await this.postClub();
                console.log('index: ', this.editedIndex);
                this.snackText = this.editedIndex == -1 ? "Club Updated" : "Club Added";
                this.snackColor = "primary";                
                this.initialize(); 
            } catch (error) {
                error.response ? console.log(error.response) : console.log(error);
                let errorMessage = error.response ? error.response.data?.error : "An Error Has Occured, Could not add the club";
                this.snackText = errorMessage;
                this.snackColor = "red";
            }
            this.overlay = false;
            this.$nextTick(() => {
                this.snackbar = true;
            })
        },

        resetValidation () {
            this.$refs.form.resetValidation();
        },

        closeAddEditSubjects () {
            this.$emit('dialog-close');
        }
    }
}
</script>